import { Helmet } from 'react-helmet-async';
import { createSeoHelper } from '@drzach/website-toolkit';
import type { SeoProps } from '@drzach/website-toolkit';
import { SITE } from '../data/site';

type SiteSeoProps = SeoProps & {
  ogType?: 'website' | 'article' | 'profile';
  /**
   * Use the title exactly as given. The toolkit helper otherwise appends
   * " | Van Every Family Chiropractic Center" (39 chars), which pushed every
   * condition-page title far past the ~60-char SERP display budget.
   */
  exactTitle?: boolean;
};

const seoDomain = SITE.domain.startsWith('www.') ? SITE.domain : `www.${SITE.domain}`;
const seoOrigin = `https://${seoDomain}`;
const getSeoData = createSeoHelper({ ...SITE, domain: seoDomain });

export default function Seo({ ogType = 'website', exactTitle = false, ...props }: SiteSeoProps) {
  const seoData = getSeoData(props);
  const { metaDescription, canonical: rawCanonical, ogImage, robotsContent } = seoData;
  const fullTitle = exactTitle && props.title ? props.title : seoData.fullTitle;
  const defaultOgImage = `${seoOrigin}/images/home-og.webp`;
  // Cloudflare Pages serves prerendered pages at trailing-slash URLs (a
  // slash-less hit 308s to the slash form), so the canonical must use the
  // final trailing-slash URL or it points at a redirect.
  const canonical =
    rawCanonical && !/\.[a-z0-9]+$/i.test(rawCanonical) && !rawCanonical.endsWith('/')
      ? `${rawCanonical}/`
      : rawCanonical;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {canonical && <link rel="canonical" href={canonical} />}
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:type" content={ogType} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImage || defaultOgImage} />
      {canonical && <meta property="og:url" content={canonical} />}
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage || defaultOgImage} />
      {robotsContent && <meta name="robots" content={robotsContent} />}
    </Helmet>
  );
}
